<template>
  <b-card no-body>
    <b-card-body id="taxes-card-body">
      <taxes-filter :values="filterValues" :filter-submitting="isLoading" @filter="setFilter" />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <table-no-result-found slot="emptystate" />
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'applicationScope'">
            {{ getApplicationScopeInfo(props.row.applicationScope) }}
          </span>
          <span v-else-if="props.column.field === 'description'">
            {{ props.row.description || '-' }}
          </span>
          <span v-else-if="props.column.field === 'rate'">
            {{ props.row.rate }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill :variant="props.row.status ? 'success' : 'danger'">
              {{ props.row.status ? t('Active') : t('Passive') }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'createdAt'">
            {{ props.row.createdAt }}
          </span>
          <span v-else-if="props.column.field === 'updatedAt'">
            {{ props.row.updatedAt || '-' }}
          </span>
          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="detailTaxes(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="t('Edit Taxes')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-button
                :id="`delete-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteTaxes(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-button-${props.row.id}`"
                  :title="t('Delete Taxes')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BBadge, BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import TaxesFilter from '@/views/pages/taxes/TaxesFilter.vue';
import useService from '@/auth/service/useService';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    TaxesFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      totalRecords: 0,
      taxApplicationScopes: [],
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        description: '',
        application_scope: '',
        status: ''
      },
      defaultFilterValues: {
        description: '',
        application_scope: '',
        status: ''
      }
    };
  },
  computed: {
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Scope'),
          field: 'applicationScope'
        },
        {
          label: this.$t('Tax Name'),
          field: 'description'
        },
        {
          label: this.$t('Rate'),
          field: 'rate'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Created At'),
          field: 'createdAt'
        },
        {
          label: this.$t('Updated At'),
          field: 'updatedAt'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    const scopes = await this.getTaxApplicationScope();
    this.taxApplicationScopes = scopes || [];
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      this.isLoading = true;
      useService.taxesService
        .getAllTaxes(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getApplicationScopeInfo(value) {
      if (!this.taxApplicationScopes || !this.taxApplicationScopes.length) return value || '-';
      const found = this.taxApplicationScopes.find(item => item && item.value === value);
      return (found && found.info) || '-';
    },
    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },
    detailTaxes(id) {
      this.$router.push({ name: 'taxes_detail', params: { id } });
    },
    deleteTaxes(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Taxes Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService.taxesService.deleteTaxes(id).then(response => {
            this.showSuccessMessage(`${response.message}`);

            const index = this.rows.findIndex(item => item.id === id);
            this.rows.splice(index, 1);
          });
        }
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
